/**
 * contentOverlay.js — Phase 2 (Content Enrichment) D3
 *
 * Runtime resolver for the content overlay JSON emitted by
 * MRT upgrade\build_content_overlay.py.
 *
 * The overlay layers crawl-sourced content (hero image, description,
 * features, spec table, downloads, sourceUrl) on top of the shell taxonomy
 * without touching the R1/R2/S1 configs. Consumers ask for content by
 * slug (product-line / category / subcategory) or by product itemCode
 * and receive a merged block — overlay first, `content-overrides.json`
 * hand-overrides last (highest priority).
 *
 * Cross-linking philosophy: `sourceUrl` is a first-class field — surfaces
 * as "Read more on Sandvik.com ↗" on every enriched page.
 *
 * @module data/contentOverlay
 * @since 2026-07-08 (feat/content-enrichment)
 */

const OVERLAY_URL = '/data/content-overlay.json';
const OVERRIDES_URL = '/data/content-overrides.json'; // optional; missing is fine

let _overlayCache = null;      // { productLines, categories, subcategories, products }
let _overridesCache = null;    // same shape
let _loadPromise = null;

async function _fetchOptional(url) {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

/**
 * Load and cache the content overlay (+ optional hand-overrides).
 * Idempotent — first caller triggers fetch, later callers share the same promise.
 * Returns null-safe: on any fetch failure the resolver falls back cleanly.
 */
export async function loadContentOverlay() {
  if (_overlayCache !== null) return _overlayCache;
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    const [overlay, overrides] = await Promise.all([
      _fetchOptional(OVERLAY_URL),
      _fetchOptional(OVERRIDES_URL),
    ]);
    _overlayCache = overlay || { productLines: {}, categories: {}, subcategories: {}, products: {} };
    _overridesCache = overrides || { productLines: {}, categories: {}, subcategories: {}, products: {} };
    return _overlayCache;
  })();

  return _loadPromise;
}

/**
 * Test-only cache seeder. Call with an overlay object to bypass fetch;
 * pass null to reset.
 * @internal
 */
export function __setOverlayCacheForTests(overlay, overrides) {
  _overlayCache = overlay;
  _overridesCache = overrides || { productLines: {}, categories: {}, subcategories: {}, products: {} };
  _loadPromise = null;
}

/**
 * Merge overlay entry with override entry — override wins field by field.
 * Both may be null. Returns null if both are null/undefined.
 */
function _merge(base, override) {
  if (!base && !override) return null;
  if (!base) return { ...override };
  if (!override) return { ...base };
  return { ...base, ...override };
}

/**
 * Look up content for a slug. `level` optional — defaults to searching
 * subcategories → categories → productLines in that order (hierarchical
 * fallback so a subcategory without its own entry can inherit its
 * category's, and a category without one inherits its product-line's).
 *
 * @param {string} slug
 * @param {{ level?: 'productLines'|'categories'|'subcategories' }} [opts]
 * @returns {object|null}
 */
export function getContentForSlug(slug, { level } = {}) {
  if (!slug || !_overlayCache) return null;
  const order = level ? [level] : ['subcategories', 'categories', 'productLines'];
  for (const lvl of order) {
    const base = _overlayCache?.[lvl]?.[slug];
    const override = _overridesCache?.[lvl]?.[slug];
    const merged = _merge(base, override);
    if (merged) return merged;
  }
  return null;
}

/**
 * Look up content for a product PDP by itemCode.
 * @param {string} itemCode
 * @returns {object|null}
 */
export function getContentForProduct(itemCode) {
  if (!itemCode || !_overlayCache) return null;
  const base = _overlayCache?.products?.[itemCode];
  const override = _overridesCache?.products?.[itemCode];
  return _merge(base, override);
}

/**
 * Convenience: has ANY overlay data been loaded? Consumer components can
 * skip rendering a "loading" state when this returns true and any lookup
 * returned null.
 */
export function hasOverlayLoaded() {
  return _overlayCache !== null;
}
